import { logger } from "@/modules/logger";
import { PublicAppSubStoreArgs, PublicAppSubStore } from "@/store/types";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { MetricsBrowserMetadata } from "@/domains/metrics";
import { asyncResultModule } from "@/modules/async-result";
import { AsyncResult } from "@/modules/async-result/types";
import { BaseError } from "@/domains/errors";
import { electronModule } from "@/modules/electron";
import { objectModule } from "@/modules/object";
import { metricsModule } from "@/modules/metrics";
import { CacheKey } from "@/modules/cache/constants";
import { snakeCase } from "lodash-es";
import { DateTime } from "luxon";
import { ElectronAppInfo, RecallSdkState, RecallSdkStatePayload } from "@/modules/electron/types";
import { AppArchitecture } from "@/store/electron/electron-store.constants";

/**
 * Renderer-owned snapshot of the latest active Recall-detected meeting window.
 */
export type LatestDetectedMeetingWindow = {
  windowId: string;
  calendarEventId: string | null;
};

export class AppStoreElectronStore extends PublicAppSubStore {
  initializationState: AsyncResult<boolean> = asyncResultModule.setLoading();
  metricsBrowserMetadataUnsubscribe: (() => void) | null = null;
  recallSdkStateUnsubscribe: (() => void) | null = null;
  recallSdkState: RecallSdkState = RecallSdkState.Idle;
  recallSdkStateErrorMessage: string | null = null;
  meetingDetectionDebugEvents = new Array<{
    kind: string;
    timestamp: DateTime;
    data: Record<string, unknown>;
  }>();
  /**
   * Latest open Recall-detected meeting window seen by the renderer.
   *
   * Top-bar voice starts read this synchronously so the UI does not wait on an
   * Electron round trip before starting a recording with speaker attribution.
   */
  latestDetectedMeetingWindow: LatestDetectedMeetingWindow | null = null;
  electronAppInfo: ElectronAppInfo | null = null;

  constructor(injectedDeps: PublicAppSubStoreArgs) {
    super(injectedDeps);

    makeObservable<
      AppStoreElectronStore,
      | "_handleBrowserMetadataUpdate"
      | "_syncElectronAppInfoFromElectron"
      | "_handleRecallSdkStateUpdate"
      | "_syncRecallSdkStateFromElectron"
    >(this, {
      initializationState: observable,
      meetingDetectionDebugEvents: observable,
      electronAppInfo: observable,
      resetState: action,
      initialize: action,
      isIntelMacDesktop: computed,
      unsubscribeFromElectronHandlers: action,
      initializeElectronHandlers: action,
      _syncElectronAppInfoFromElectron: action,
      _handleBrowserMetadataUpdate: action,
      metricsBrowserMetadataUnsubscribe: observable,
      recallSdkState: observable,
      recallSdkStateErrorMessage: observable,
      _syncRecallSdkStateFromElectron: action,
      _handleRecallSdkStateUpdate: action,
      pushRecallDebugEvent: action,
      latestDetectedMeetingWindow: observable.ref,
      setLatestDetectedMeetingWindow: action,
      clearLatestDetectedMeetingWindow: action,
      recallSdkStateUnsubscribe: observable,
    });
  }

  initialize = async () => {
    try {
      runInAction(() => {
        this.initializationState = asyncResultModule.setLoading();
      });

      await this._syncElectronAppInfoFromElectron();
      await this._syncRecallSdkStateFromElectron();
      await this.initializeElectronHandlers();

      runInAction(() => {
        this.initializationState = asyncResultModule.setReady(true);
      });
    } catch (unknownErr) {
      const err = unknownErr as BaseError;

      logger.error({
        message: "[AppStoreElectronStore] Failed to initialize.",
        info: { error: objectModule.safeErrorAsJson(err) },
      });

      runInAction(() => {
        this.initializationState = asyncResultModule.setError(err);
      });

      throw err;
    }
  };

  async initializeElectronHandlers() {
    if (!this.store.interface.isDesktop) return null;

    try {
      this.metricsBrowserMetadataUnsubscribe?.();

      const unsubscribe = await electronModule.onBrowserMetadata(async metadata => {
        await this._handleBrowserMetadataUpdate(metadata);
      });

      this.metricsBrowserMetadataUnsubscribe = unsubscribe ?? null;
    } catch (error) {
      logger.warn({
        message: "[AppStoreElectronStore] Failed to subscribe to browser metadata updates.",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });
    }

    try {
      this.recallSdkStateUnsubscribe?.();

      const unsubscribe = await electronModule.recall.onSdkState?.({
        callback: payload => {
          runInAction(() => {
            this._handleRecallSdkStateUpdate(payload);
          });
        },
      });

      this.recallSdkStateUnsubscribe = unsubscribe ?? null;
    } catch (error) {
      logger.warn({
        message: "[AppStoreElectronStore] Failed to subscribe to recall sdk state updates.",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });
    }
  }

  get isIntelMacDesktop(): boolean {
    if (!this.electronAppInfo || this.store.interface.isWindows) return false;
    return this.electronAppInfo.electronSystemArchitecture === AppArchitecture.Intel;
  }

  private async _handleBrowserMetadataUpdate(metadata: MetricsBrowserMetadata) {
    if (!this.store.interface.isDesktop) return;

    logger.debug({
      message: "[AppStoreElectronStore][_handleBrowserMetadataUpdate] Event received.",
      info: { metadata },
    });

    const metadataKeys = new Set<CacheKey>();

    try {
      if (metadata.segmentAnonymousId) {
        metadataKeys.add(CacheKey.MetricsSegmentAnonymousId);

        await metricsModule.setSegmentAnonymousId({
          segmentAnonymousId: metadata.segmentAnonymousId,
        });
      }

      if (metadata.gaClientId) {
        metadataKeys.add(CacheKey.MetricsGoogleAnalyticsClientId);

        await metricsModule.setGoogleAnalyticsClientId({
          googleAnalyticsClientId: metadata.gaClientId,
        });
      }

      if (metadata.gaSessionId) {
        metadataKeys.add(CacheKey.MetricsGoogleAnalyticsSessionId);

        await metricsModule.setGoogleAnalyticsSessionId({
          googleAnalyticsSessionId: metadata.gaSessionId,
        });
      }

      if (metadataKeys.size > 0) {
        const metadataString = Array.from(metadataKeys)
          .map(val => snakeCase(val))
          .join(";");

        await metricsModule.setMetadataString({
          metadataString,
        });
      }
    } catch (unknownErr) {
      const error = unknownErr as BaseError;

      logger.warn({
        message: "[AppStoreElectronStore][_handleBrowserMetadataUpdate] Failed to update metrics.",
        info: { error: objectModule.safeErrorAsJson(error) },
      });
    }
  }

  async unsubscribeFromElectronHandlers() {
    this.metricsBrowserMetadataUnsubscribe?.();
    this.metricsBrowserMetadataUnsubscribe = null;
    this.recallSdkStateUnsubscribe?.();
    this.recallSdkStateUnsubscribe = null;
  }

  private async _syncElectronAppInfoFromElectron(): Promise<ElectronAppInfo | null> {
    if (!this.store.interface.isDesktop) return null;

    try {
      const electronAppInfo = await electronModule.getElectronAppInfo();

      if (!electronAppInfo) {
        return null;
      }

      logger.info({
        message:
          "[AppStoreElectronStore][_syncElectronAppInfoFromElectron] Electron app info synced.",
        info: { electronAppInfo },
      });

      runInAction(() => {
        this.electronAppInfo = electronAppInfo;
      });

      await metricsModule.setElectronAppInfo({
        electronAppInfo,
      });

      return electronAppInfo;
    } catch (error) {
      logger.warn({
        message: "[AppStoreElectronStore][_syncElectronAppInfoFromElectron] Failed.",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });

      return null;
    }
  }

  private async _syncRecallSdkStateFromElectron() {
    if (!this.store.interface.isDesktop) return;

    try {
      const payload = await electronModule.recall.getSdkState();

      runInAction(() => {
        this._handleRecallSdkStateUpdate(payload);
      });
    } catch (error) {
      logger.warn({
        message: "[AppStoreElectronStore][_syncRecallSdkStateFromElectron] Failed.",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });
    }
  }

  private _handleRecallSdkStateUpdate(payload: RecallSdkStatePayload) {
    this.recallSdkState = payload.state;
    this.recallSdkStateErrorMessage = payload.errorMessage ?? null;
  }

  pushRecallDebugEvent = (data: Record<string, unknown>, kind: string) => {
    if (!this.store.interface.isDesktop) return;

    this.meetingDetectionDebugEvents.push({ data, kind, timestamp: DateTime.now() });
    const excessEvents =
      this.meetingDetectionDebugEvents.length - MAX_MEETING_DETECTION_DEBUG_EVENTS;
    if (excessEvents > 0) {
      this.meetingDetectionDebugEvents.splice(0, excessEvents);
    }
  };

  setLatestDetectedMeetingWindow = ({ windowId, calendarEventId }: LatestDetectedMeetingWindow) => {
    if (!this.store.interface.isDesktop) return;

    this.latestDetectedMeetingWindow = {
      windowId,
      calendarEventId,
    };
  };

  clearLatestDetectedMeetingWindow = ({ windowId }: { windowId?: string } = {}) => {
    if (!this.latestDetectedMeetingWindow) return;
    if (windowId && this.latestDetectedMeetingWindow.windowId !== windowId) return;

    this.latestDetectedMeetingWindow = null;
  };

  resetState = async () => {
    await this.unsubscribeFromElectronHandlers();

    this.recallSdkState = RecallSdkState.Idle;
    this.recallSdkStateErrorMessage = null;
    this.latestDetectedMeetingWindow = null;

    await Promise.all([
      metricsModule.clearSegmentAnonymousId(),
      metricsModule.clearGoogleAnalyticsClientId(),
      metricsModule.clearGoogleAnalyticsSessionId(),
      metricsModule.clearElectronAppInfo(),
    ]);

    runInAction(() => {
      this.electronAppInfo = null;
    });
  };
}

const MAX_MEETING_DETECTION_DEBUG_EVENTS = 30;
